import {
  useAddToConversation,
  useGetPosts,
  useGetPractices,
} from '@/hooks/queries/practices';
import useGetPromptById from '@/hooks/queries/prompts/useGetPromptById';
import { GenerationGuardProvider } from '@/contexts/generation-guard-context';
import { useInitializePractice } from '@/hooks/useInitializePractice';
import { useRunActionState } from '@/hooks/useRunActionState';
import { useUserStore } from '@/store/use-user-store';
import { useAuthStore } from '@/store/use-auth-store';
import { isLoggingOutSession } from '@/utils/auth-session';
import { useChatStore } from '@/store/useChatStore';
import { Message, Post, PromptData } from '@/types/agent';
import { ChatMode } from '@/utils/constants';
import useGetPrompts from 'Src/hooks/queries/prompts/useGetPrompts';
import { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import RealTimeAgent from '../realtime/realtime-agent';
import ChatContainer from './components/chat-container';

// TODO: REFRACT THIS COMPONENT - REMOV EFFECTS NOT NEEDED

function promptMatchesAction(
  candidate: PromptData | null | undefined,
  actionTitle: string | null | undefined
): boolean {
  if (!candidate) return false;
  if (!actionTitle) return true;
  return candidate.name?.toLowerCase() === actionTitle.toLowerCase();
}

function ChatPage({ defaultAgent }: { defaultAgent?: string }) {
  const { chatMode, setChatMode, addMessages } = useChatStore();
  const { selectedPrompt, setSelectedPrompt } = useChatStore();
  const { createPracticeAndNavigate, prompts } =
    useInitializePractice(defaultAgent);
  const { isLoading: fetchingPrompts } = useGetPrompts();
  const { runActionState } = useRunActionState();
  const { id: practiceId } = useParams();
  const location = useLocation();
  const { data: prompt } = useGetPromptById(
    selectedPrompt?.id != null ? String(selectedPrompt.id) : undefined,
    true
  );
  const { refetch: refetchPosts } = useGetPosts(Number(practiceId));
  const userId = useUserStore((state) => state.user?.id);
  const { refetch: refetchPractices } = useGetPractices(
    { has_posts: true, user_id: userId },
    false
  );

  // selectedPrompt lives in a shared store, so it can still hold the agent of
  // a previously opened chat. Only trust it when it matches this chat's action.
  const expectedActionTitle =
    new URLSearchParams(location.search).get('action') ||
    runActionState?.actionTitle;
  const activeSelectedPrompt = promptMatchesAction(
    selectedPrompt,
    expectedActionTitle
  )
    ? selectedPrompt
    : null;
  const activePrompt =
    prompt && prompt.id === activeSelectedPrompt?.id ? prompt : undefined;

  // Must run before the effects below that select this chat's prompt.
  useEffect(() => {
    const current = useChatStore.getState().selectedPrompt;
    if (current && !promptMatchesAction(current, expectedActionTitle)) {
      setSelectedPrompt(null);
    }
  }, [expectedActionTitle, practiceId, setSelectedPrompt]);

  // Update selectedPrompt when prompt data changes
  useEffect(() => {
    if (prompt && useChatStore.getState().selectedPrompt?.id === prompt.id) {
      setSelectedPrompt(prompt);
    }
  }, [prompt, setSelectedPrompt]);

  useEffect(() => {
    return () => {
      if (isLoggingOutSession()) return;
      if (!useAuthStore.getState().isAuthenticated) return;
      void refetchPractices();
    };
  }, [refetchPractices]);

  const addToConversationMutation = useAddToConversation({
    onSuccess: () => {
      refetchPosts();
    },
  });

  const redirectToChat = async (
    messages: Message[],
    fallbackMode = ChatMode.TEXT
  ) => {
    addMessages([...messages]);
    const conversationPosts: Post[] = messages
      .filter((message) => !(message.isUser && !message.text))
      .map((message) => ({
        text: message.text,
        text_src: message.isUser ? 'User' : 'Assistant',
      }));

    if (conversationPosts.length > 0 && practiceId) {
      await addToConversationMutation.mutateAsync({
        practiceId: Number(practiceId),
        posts: { posts: conversationPosts },
      });
      // trigger user variable extraction
      // triggerExtractionMutation.mutate({
      //   practice_id: Number(practiceId),
      //   prompt_id: selectedPrompt?.id,
      // });
    }
    setChatMode(fallbackMode);
  };

  // useEffect(() => {
  //   if (chatMode === ChatMode.REAL_TIME) {
  //     console.log(prompts, 'prompts');
  //     const prompt = getPromptIdByName(prompts, runActionState?.actionTitle);
  //     clearMessages();
  //     setSelectedPrompt(prompt);
  //   }
  // }, [chatMode]);

  useEffect(() => {
    if (!runActionState?.actionTitle || !prompts?.results) return;

    const matchedPrompt = prompts.results.find(
      (entry) =>
        entry.name?.toLowerCase() === runActionState.actionTitle.toLowerCase()
    );
    if (matchedPrompt) {
      setSelectedPrompt(matchedPrompt);
    }
  }, [prompts?.results, runActionState?.actionTitle, setSelectedPrompt]);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const actionTitle = queryParams.get('action');
    const tagFromUrl = queryParams.get('tag');
    if (!actionTitle || !prompts?.results) return;

    const matchedPrompt = prompts.results.find(
      (entry) => entry.name?.toLowerCase() === actionTitle.toLowerCase()
    );
    if (matchedPrompt) {
      setSelectedPrompt({
        ...matchedPrompt,
        tag: tagFromUrl || matchedPrompt.tag,
      });
    }
  }, [location.search, prompts?.results, setSelectedPrompt]);

  return (
    <GenerationGuardProvider>
      <div
        className="mx-auto flex-1 flex flex-col w-full max-w-full bg-white overflow-hidden"
        key={practiceId}
      >
        {chatMode === ChatMode.REAL_TIME &&
        activeSelectedPrompt &&
        activePrompt &&
        practiceId ? (
          <RealTimeAgent
            enable={true}
            redirectToChat={redirectToChat}
            selectedPrompt={activePrompt}
          />
        ) : (
          <ChatContainer
            fetchingPrompts={fetchingPrompts}
            runActionState={runActionState}
            fetchingLatetConversation={addToConversationMutation.isPending}
            restartPractice={(title) =>
              createPracticeAndNavigate({ name: title })
            }
            selectedPrompt={activePrompt ?? activeSelectedPrompt}
          />
        )}
      </div>
    </GenerationGuardProvider>
  );
}

export default ChatPage;
